import React, { useState, useEffect } from 'react';
import {
  Card,
  Steps,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Divider,
  Alert,
  message,
  Spin,
  InputNumber,
  Checkbox,
  Typography,
} from 'antd';
import {
  UserOutlined,
  BankOutlined,
  ShopOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { OnboardingService } from '../services/onboarding';
import {
  CustomOnboardingRequest,
  OnboardingForm,
  CustomOnboardingResponse,
} from '../types';

const { Step } = Steps;
const { Title, Text } = Typography;
const { TextArea } = Input;

interface Props {
  onSuccess?: (response: CustomOnboardingResponse) => void;
  onCancel?: () => void;
}

const SUPPORTED_COUNTRIES = [
  { value: 'US', label: '美国' },
  { value: 'GB', label: '英国' },
  { value: 'CA', label: '加拿大' },
  { value: 'AU', label: '澳大利亚' },
  { value: 'DE', label: '德国' },
  { value: 'FR', label: '法国' },
  { value: 'JP', label: '日本' },
  { value: 'SG', label: '新加坡' },
  { value: 'HK', label: '香港' },
];

const US_STATES = [
  { value: 'CA', label: 'California' },
  { value: 'NY', label: 'New York' },
  { value: 'TX', label: 'Texas' },
  { value: 'FL', label: 'Florida' },
  { value: 'IL', label: 'Illinois' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'OH', label: 'Ohio' },
  { value: 'GA', label: 'Georgia' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'MI', label: 'Michigan' },
  // ... 其他州
];

// 常用商户类别码 (MCC)
const MCC_CODES = [
  { value: '5411', label: '5411 - 超市/杂货店' },
  { value: '5812', label: '5812 - 餐饮' },
  { value: '5814', label: '5814 - 快餐店' },
  { value: '5912', label: '5912 - 药店' },
  { value: '5942', label: '5942 - 书店' },
  { value: '5999', label: '5999 - 杂货零售' },
  { value: '7299', label: '7299 - 其他服务' },
  { value: '7372', label: '7372 - 软件服务' },
  { value: '7399', label: '7399 - 商业服务' },
  { value: '7922', label: '7922 - 票务服务' },
  { value: '7991', label: '7991 - 旅游景点' },
  { value: '7999', label: '7999 - 娱乐服务' },
  { value: '8011', label: '8011 - 医疗服务' },
  { value: '8099', label: '8099 - 健康服务' },
  { value: '8299', label: '8299 - 教育服务' },
];

const CustomOnboardingForm: React.FC<Props> = ({ onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formConfig, setFormConfig] = useState<OnboardingForm | null>(null);
  const [businessType, setBusinessType] = useState<string>('individual');
  const [country, setCountry] = useState<string>('US');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CustomOnboardingResponse | null>(null);

  // 获取表单配置
  useEffect(() => {
    const loadFormConfig = async () => {
      setLoading(true);
      try {
        const config = await OnboardingService.getCustomOnboardingForm(country, businessType);
        setFormConfig(config);
      } catch (error) {
        console.error('Failed to load form config:', error);
      } finally {
        setLoading(false);
      }
    };
    loadFormConfig();
  }, [country, businessType]);

  const steps = [
    {
      title: '基本信息',
      icon: <UserOutlined />,
      description: '邮箱、国家、业务类型',
    },
    {
      title: businessType === 'individual' ? '个人信息' : '公司信息',
      icon: <UserOutlined />,
      description: businessType === 'individual' ? '姓名、地址、身份信息' : '公司名称、税号、地址',
    },
    {
      title: '业务资料',
      icon: <ShopOutlined />,
      description: '业务网站、产品描述',
    },
    {
      title: '银行账户',
      icon: <BankOutlined />,
      description: '收款银行账户信息',
    },
    {
      title: '确认提交',
      icon: <SafetyOutlined />,
      description: '同意服务条款并提交',
    },
  ];

  const nextStep = async () => {
    try {
      await form.validateFields();
      setCurrentStep(currentStep + 1);
    } catch (error) {
      message.error('请填写完整信息');
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      setSubmitting(true);

      const values = form.getFieldsValue(true);

      // 构建请求数据
      const request: CustomOnboardingRequest = {
        email: values.email,
        country: values.country,
        business_type: values.business_type,
        tos_acceptance: {
          date: Math.floor(Date.now() / 1000),
          ip: '0.0.0.0', // 后端会获取真实IP
          user_agent: navigator.userAgent,
        },
      };

      // 添加个人信息
      if (values.business_type === 'individual') {
        request.individual = {
          first_name: values.first_name,
          last_name: values.last_name,
          phone: values.phone,
          dob: values.dob_year ? {
            day: values.dob_day,
            month: values.dob_month,
            year: values.dob_year,
          } : undefined,
          address: {
            line1: values.address_line1,
            line2: values.address_line2 || '',
            city: values.address_city,
            state: values.address_state,
            postal_code: values.address_postal_code,
            country: values.country,
          },
          ssn_last_4: values.ssn_last_4,
        };
      }

      // 添加公司信息
      if (values.business_type === 'company') {
        request.company = {
          name: values.company_name,
          tax_id: values.company_tax_id,
          phone: values.company_phone,
          address: {
            line1: values.company_address_line1,
            line2: values.company_address_line2 || '',
            city: values.company_address_city,
            state: values.company_address_state,
            postal_code: values.company_address_postal_code,
            country: values.country,
          },
        };
      }

      // 添加业务资料
      request.business_profile = {
        mcc: values.mcc,
        url: values.business_url,
        statement_descriptor: values.statement_descriptor,
        product_description: values.product_description,
        support_phone: values.support_phone,
        support_email: values.support_email,
      };

      // 添加银行账户信息
      request.bank_account = {
        country: values.country,
        currency: values.country === 'US' ? 'usd' : 'eur',
        account_holder_name: values.account_holder_name,
        account_holder_type: values.business_type === 'individual' ? 'individual' : 'company',
        account_number: values.account_number,
        routing_number: values.routing_number,
      };

      const response = await OnboardingService.submitCustomOnboarding(request);
      setResult(response);

      if (response.success) {
        message.success('入驻申请提交成功！');
        onSuccess?.(response);
      } else {
        message.error(response.message || '提交失败');
      }
    } catch (error: any) {
      message.error(error.message || '提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 基本信息步骤
  const renderBasicInfoStep = () => (
    <>
      <Form.Item
        name="email"
        label="邮箱地址"
        rules={[
          { required: true, message: '请输入邮箱地址' },
          { type: 'email', message: '请输入有效的邮箱地址' },
        ]}
      >
        <Input size="large" placeholder="your@email.com" />
      </Form.Item>

      <Form.Item
        name="country"
        label="国家/地区"
        rules={[{ required: true, message: '请选择国家/地区' }]}
        initialValue="US"
      >
        <Select
          size="large"
          placeholder="选择国家/地区"
          onChange={(value) => setCountry(value)}
        >
          {SUPPORTED_COUNTRIES.map((c) => (
            <Select.Option key={c.value} value={c.value}>
              {c.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="business_type"
        label="业务类型"
        rules={[{ required: true, message: '请选择业务类型' }]}
        initialValue="individual"
      >
        <Select
          size="large"
          placeholder="选择业务类型"
          onChange={(value) => setBusinessType(value)}
        >
          <Select.Option value="individual">个人</Select.Option>
          <Select.Option value="company">公司</Select.Option>
          <Select.Option value="non_profit">非营利组织</Select.Option>
        </Select>
      </Form.Item>
    </>
  );

  // 个人信息步骤
  const renderPersonalInfoStep = () => (
    <>
      {businessType === 'individual' ? (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="first_name"
                label="名"
                rules={[{ required: true, message: '请输入名' }]}
              >
                <Input size="large" placeholder="First Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="last_name"
                label="姓"
                rules={[{ required: true, message: '请输入姓' }]}
              >
                <Input size="large" placeholder="Last Name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="phone" label="电话号码">
            <Input size="large" placeholder="+1 (555) 123-4567" />
          </Form.Item>

          {country === 'US' && (
            <>
              <Divider>出生日期</Divider>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="dob_month"
                    label="月"
                    rules={[{ required: true, message: '请选择月份' }]}
                  >
                    <InputNumber size="large" min={1} max={12} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="dob_day"
                    label="日"
                    rules={[{ required: true, message: '请选择日期' }]}
                  >
                    <InputNumber size="large" min={1} max={31} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="dob_year"
                    label="年"
                    rules={[{ required: true, message: '请输入年份' }]}
                  >
                    <InputNumber size="large" min={1900} max={2010} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Divider>地址信息</Divider>
          <Form.Item
            name="address_line1"
            label="地址行1"
            rules={[{ required: true, message: '请输入地址' }]}
          >
            <Input size="large" placeholder="123 Main Street" />
          </Form.Item>
          <Form.Item name="address_line2" label="地址行2">
            <Input size="large" placeholder="Apt, Suite, etc. (optional)" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="address_city"
                label="城市"
                rules={[{ required: true, message: '请输入城市' }]}
              >
                <Input size="large" placeholder="City" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="address_state"
                label="州/省"
                rules={[{ required: true, message: '请选择州/省' }]}
              >
                {country === 'US' ? (
                  <Select size="large" placeholder="State">
                    {US_STATES.map((s) => (
                      <Select.Option key={s.value} value={s.value}>
                        {s.label}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Input size="large" placeholder="State/Province" />
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="address_postal_code"
                label="邮编"
                rules={[{ required: true, message: '请输入邮编' }]}
              >
                <Input size="large" placeholder="12345" />
              </Form.Item>
            </Col>
          </Row>

          {country === 'US' && (
            <Form.Item
              name="ssn_last_4"
              label="SSN 后4位"
              rules={[
                { required: true, message: '请输入 SSN 后4位' },
                { len: 4, message: '请输入4位数字' },
              ]}
              extra="美国税务身份验证需要"
            >
              <Input size="large" maxLength={4} placeholder="1234" />
            </Form.Item>
          )}
        </>
      ) : (
        // 公司信息
        <>
          <Form.Item
            name="company_name"
            label="公司名称"
            rules={[{ required: true, message: '请输入公司名称' }]}
          >
            <Input size="large" placeholder="Company Name" />
          </Form.Item>

          {country === 'US' && (
            <Form.Item
              name="company_tax_id"
              label="税号 (EIN)"
              rules={[{ required: true, message: '请输入税号' }]}
            >
              <Input size="large" placeholder="12-3456789" />
            </Form.Item>
          )}

          <Form.Item name="company_phone" label="公司电话">
            <Input size="large" placeholder="+1 (555) 123-4567" />
          </Form.Item>

          <Divider>公司地址</Divider>
          <Form.Item
            name="company_address_line1"
            label="地址行1"
            rules={[{ required: true, message: '请输入地址' }]}
          >
            <Input size="large" placeholder="123 Business Street" />
          </Form.Item>
          <Form.Item name="company_address_line2" label="地址行2">
            <Input size="large" placeholder="Suite, Floor, etc." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="company_address_city"
                label="城市"
                rules={[{ required: true, message: '请输入城市' }]}
              >
                <Input size="large" placeholder="City" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="company_address_state"
                label="州/省"
                rules={[{ required: true, message: '请选择州/省' }]}
              >
                <Input size="large" placeholder="State" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="company_address_postal_code"
                label="邮编"
                rules={[{ required: true, message: '请输入邮编' }]}
              >
                <Input size="large" placeholder="12345" />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </>
  );

  // 业务资料步骤
  const renderBusinessProfileStep = () => (
    <>
      <Form.Item
        name="mcc"
        label="商户类别"
        rules={[{ required: true, message: '请选择商户类别' }]}
        extra="MCC (Merchant Category Code) 用于分类您的业务类型"
      >
        <Select size="large" placeholder="选择商户类别" showSearch optionFilterProp="children">
          {MCC_CODES.map((mcc) => (
            <Select.Option key={mcc.value} value={mcc.value}>
              {mcc.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="business_url"
        label="业务网站"
        rules={[
          { required: true, message: '请输入业务网站' },
          { type: 'url', message: '请输入有效的网址' },
        ]}
      >
        <Input size="large" placeholder="https://yourwebsite.com" />
      </Form.Item>

      <Form.Item
        name="statement_descriptor"
        label="账单显示名称"
        rules={[
          { required: true, message: '请输入账单显示名称' },
          { max: 22, message: '最多22个字符' },
          { pattern: /^[a-zA-Z0-9\s]+$/, message: '只允许英文字母、数字和空格' },
        ]}
        extra="显示在客户信用卡账单上的名称，最多22个字符，仅限英文"
      >
        <Input size="large" placeholder="MYCOMPANY" maxLength={22} />
      </Form.Item>

      <Form.Item
        name="product_description"
        label="产品/服务描述"
        rules={[{ required: true, message: '请描述您的产品或服务' }]}
      >
        <TextArea
          rows={4}
          placeholder="请描述您提供的产品或服务，这将帮助审核团队了解您的业务"
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="support_phone" label="客服电话">
            <Input size="large" placeholder="+1 (555) 123-4567" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="support_email"
            label="客服邮箱"
            rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
          >
            <Input size="large" placeholder="support@yourcompany.com" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  // 银行账户步骤
  const renderBankAccountStep = () => (
    <>
      <Alert
        message="银行账户信息"
        description="请确保银行账户信息准确无误，这将用于接收您的收款资金。"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form.Item
        name="account_holder_name"
        label="账户持有人姓名"
        rules={[{ required: true, message: '请输入账户持有人姓名' }]}
      >
        <Input size="large" placeholder="John Doe" />
      </Form.Item>

      <Form.Item
        name="account_number"
        label="银行账户号码"
        rules={[{ required: true, message: '请输入银行账户号码' }]}
      >
        <Input size="large" placeholder="000123456789" />
      </Form.Item>

      {country === 'US' && (
        <Form.Item
          name="routing_number"
          label="Routing Number"
          rules={[
            { required: true, message: '请输入 Routing Number' },
            { len: 9, message: 'Routing Number 应为9位数字' },
          ]}
        >
          <Input size="large" placeholder="110000000" maxLength={9} />
        </Form.Item>
      )}
    </>
  );

  // 确认提交步骤
  const renderConfirmStep = () => (
    <>
      {result ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
          <Title level={3}>入驻申请已提交！</Title>
          <Text type="secondary">
            您的申请正在审核中，审核结果将通过邮件通知您。
          </Text>
        </div>
      ) : (
        <>
          <Alert
            message="请仔细阅读并同意以下条款"
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            name="tos_acceptance"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error('请阅读并同意服务条款')),
              },
            ]}
          >
            <Checkbox>
              我已阅读并同意{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">
                服务条款
              </a>{' '}
              和{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">
                隐私政策
              </a>
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="stripe_tos"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error('请同意收单通道服务条款')),
              },
            ]}
          >
            <Checkbox>
              我同意{' '}
              <a
                href="https://stripe.com/connect-account/legal"
                target="_blank"
                rel="noopener noreferrer"
              >
                收单通道账户服务协议
              </a>
            </Checkbox>
          </Form.Item>

          <Divider />

          <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
            <Text strong>提交后：</Text>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>您的信息将提交至收单通道进行审核</li>
              <li>审核通常需要 1-3 个工作日</li>
              <li>审核通过后即可开始接收支付</li>
            </ul>
          </div>
        </>
      )}
    </>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderBasicInfoStep();
      case 1:
        return renderPersonalInfoStep();
      case 2:
        return renderBusinessProfileStep();
      case 3:
        return renderBankAccountStep();
      case 4:
        return renderConfirmStep();
      default:
        return null;
    }
  };

  return (
    <Card>
      <Steps current={currentStep} style={{ marginBottom: 32 }}>
        {steps.map((step) => (
          <Step key={step.title} title={step.title} description={step.description} />
        ))}
      </Steps>

      <Spin spinning={loading || submitting}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ maxWidth: 600, margin: '0 auto' }}
        >
          {renderStepContent()}

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {currentStep > 0 && !result && (
              <Button size="large" onClick={prevStep}>
                上一步
              </Button>
            )}
            <div style={{ marginLeft: 'auto' }}>
              {onCancel && (
                <Button size="large" onClick={onCancel} style={{ marginRight: 16 }}>
                  取消
                </Button>
              )}
              {currentStep < steps.length - 1 ? (
                <Button type="primary" size="large" onClick={nextStep}>
                  下一步
                </Button>
              ) : !result ? (
                <Button
                  type="primary"
                  size="large"
                  loading={submitting}
                  onClick={handleSubmit}
                >
                  提交申请
                </Button>
              ) : null}
            </div>
          </div>
        </Form>
      </Spin>
    </Card>
  );
};

export default CustomOnboardingForm;
